import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { IListLayout } from 'src/app/models/interfaces/list-layout.interface';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { ListLayoutBoxesFormContext } from '../list-layout-boxes-form-context';

@Component({
    selector: 'app-list-layout-boxes-form',
    templateUrl: './list-layout-boxes-form.component.html',
    styleUrls: ['./list-layout-boxes-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatCheckboxModule,
        MatButtonModule,
        NgClass,
    ]
})
export class ListLayoutBoxesFormComponent extends ManagementFormComponent<IListLayout> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du client à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('listLayout')
    public dataInput: Required<IListLayout> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ListLayoutBoxesFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ListLayoutBoxesFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ListLayoutBoxesFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('listLayoutUpdated')
    public dataOutput: EventEmitter<IListLayout> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
        });
    }

    /**
     * Utilisé pour rendre le champ id et type read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
        this.formGroup.get('workflowStepColumnWidth').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IListLayout> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            organizationId: new UntypedFormControl(null),
            name: new UntypedFormControl(null, Validators.required),
            isScreenSizeAdapted: new UntypedFormControl(null),
            workflowStepColumnWidth: new UntypedFormControl(null),
            recordsPerPage: new UntypedFormControl(null, [Validators.required, Validators.min(50)]),
            sortAttributeId: new UntypedFormControl(null),
            sortDirection: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
